import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { getApi } from "../../../api/api";
import {
  BonusBalanceInterface,
  BonusTransactionInterface,
} from "../../../common/types/interfaces";

export const useBonus = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clusterIdParam = searchParams.get("clusterId");
  const clusterId = clusterIdParam ? Number(clusterIdParam) : undefined;

  const [balances, setBalances] = useState<BonusBalanceInterface[]>([]);
  const [transactions, setTransactions] = useState<BonusTransactionInterface[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const LIMIT = 50;

  // Балансы (все кластеры)
  useEffect(() => {
    let cancelled = false;
    getApi()
      .getBonusBalances()
      .then((res) => {
        if (!cancelled) setBalances(res);
      })
      .catch(() => {
        if (!cancelled) setBalances([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Автовыбор первого кластера (если не выбран)
  useEffect(() => {
    if (clusterId !== undefined) return;
    if (balances.length === 0) return;
    const params = new URLSearchParams(searchParams);
    params.set("clusterId", String(balances[0].clusterId));
    setSearchParams(params, { replace: true });
  }, [balances, clusterId, searchParams, setSearchParams]);

  // История по выбранному кластеру
  useEffect(() => {
    if (clusterId === undefined) return;

    setOffset(0);
    setHasMore(true);
    setTransactions([]);
    setLoading(true);
  }, [clusterId]);

  useEffect(() => {
    if (clusterId === undefined) return;
    if (!hasMore && offset > 0) return;
    if (isFetching) return;

    let cancelled = false;
    setIsFetching(true);

    getApi()
      .getBonusHistory(clusterId, { limit: LIMIT, offset })
      .then((res) => {
        if (!cancelled) {
          const data = res.data || [];
          setTransactions((prev) => (offset === 0 ? data : [...prev, ...data]));
          if (data.length < LIMIT) setHasMore(false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          if (offset === 0) setTransactions([]);
          setHasMore(false);
          setLoading(false);
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId, offset]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) setOffset((prev) => prev + LIMIT);
  }, [isFetching, hasMore]);

  const selectedBalance = balances.find((b) => b.clusterId === clusterId);

  const handleSelectCluster = (id: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("clusterId", String(id));
    setSearchParams(params);
  };

  return {
    balances,
    selectedBalance,
    transactions,
    clusterId,
    handleSelectCluster,
    loading,
    ready: !loading,
    isFetching,
    hasMore,
    loadMore,
    isEmpty: !loading && transactions.length === 0,
  };
};
